import moment, { min, Moment } from 'moment';
import React, { useEffect, useLayoutEffect } from 'react';
import { createUseStyles } from 'react-jss';
import Card from 'sharedComponents/core/Cards/Card';
import { createFragmentContainer, graphql } from 'react-relay';
import {
  DateRange,
  getHistoryName,
  historyType,
  merge,
  calculateHistoryGaps
} from './helpers';

// Temporary type definition - will be replaced by generated GraphQL types
type Timeline_history = any[];

const useStyles = createUseStyles((theme) => ({
  root: {},
  labels: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 25
  },
  holder: {
    display: 'flex',
    gap: '20px'
  },
  colorContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  },
  color: {
    width: 10,
    minWidth: 10,
    height: 10,
    minHeight: 10,
    borderRadius: '100%'
  },
  colorLabel: {
    fontSize: 11,
    color: '#546e7a',
    fontWeight: 500
  },
  row: {
    height: 12,
    borderRadius: 9,
    marginBottom: 5,
    cursor: 'pointer',
    top: 0,
    position: 'absolute',
    '&:hover': {
      border: '3px solid #0101012e',
      boxShadow: '0px 2.19082px 4.38164px 0px rgba(0, 0, 0, 0.12)'
    }
  },
  chart: {},
  hoverInfo: {
    display: 'flex',
    marginTop: 0,
    alignItems: 'center',
    minHeight: 19,
    justifyContent: 'space-between'
  },
  placeName: {
    color: '#263238',
    fontSize: 12,
    fontWeight: 600
  },
  lengthThere: {
    fontSize: 12,
    fontWeight: 500,
    color: '#546e7a'
  },
  rowContainer: {
    position: 'relative',
    height: 12,
    margin: '5px 0px'
  }
}));

type Props = {
  history: Timeline_history;
  timelineStartDate: Moment;
  yearsBack: number;
  // Called with the history item behind a clicked section (undefined for gaps)
  onSectionClick?: (historyItem: any) => void;
};

const TimelineFrag = ({
  history,
  timelineStartDate,
  yearsBack,
  onSectionClick
}: Props) => {
  const classes = useStyles();

  const chartRef = React.useRef<HTMLDivElement>(null);
  const [chartWidth, setChartWidth] = React.useState(0);
  const [hoverData, setHoverData] = React.useState<{
    placeName: string;
    lengthThere: string;
  } | null>(null);

  const [dimensions, setDimensions] = React.useState({
    height: window.innerHeight,
    width: window.innerWidth
  });

  function handleResize() {
    setDimensions({
      height: window.innerHeight,
      width: window.innerWidth
    });
  }

  React.useEffect(() => {
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useLayoutEffect(() => {
    const calculateWidth = () => {
      const width = chartRef?.current?.offsetWidth;
      console.log('Timeline width:', width);

      if (!width) return;
      setChartWidth(width);
    };

    // Use a small timeout to ensure DOM is fully rendered
    const timeoutId = setTimeout(calculateWidth, 0);

    return () => clearTimeout(timeoutId);
  }, [chartRef, dimensions]);

  // Also recalculate on mount to handle initial render
  useLayoutEffect(() => {
    const calculateWidth = () => {
      const width = chartRef?.current?.offsetWidth;
      if (width && width !== chartWidth) {
        setChartWidth(width);
      }
    };

    calculateWidth();
  }, []);

  const pixelsPerDay = chartWidth / (365 * yearsBack);

  const today = timelineStartDate.clone();
  const yearsAgo = timelineStartDate.clone().subtract(yearsBack, 'years');

  type DateItem = {
    history: Timeline_history | undefined;
    dates: DateRange[];
    unverified: boolean;
    over28Days: boolean;
    unknown: boolean;
  };

  // Use the extracted gap calculation function
  const { historyDates, gaps } = calculateHistoryGaps(
    history,
    timelineStartDate,
    yearsBack
  );

  const allDates = [...historyDates, ...gaps];

  // Order allDates by the last endDate (with the dates sorted by endDate)
  allDates.sort((a, b) => {
    const j = a.dates.sort((a, b) => moment(a.endDate).diff(moment(b.endDate)));
    const k = b.dates.sort((a, b) => moment(a.endDate).diff(moment(b.endDate)));

    const aEndDate = j[j.length - 1]?.endDate;
    const bEndDate = k[k.length - 1]?.endDate;

    if (moment(aEndDate).isBefore(bEndDate)) {
      return 1;
    }
    if (moment(aEndDate).isAfter(bEndDate)) {
      return -1;
    }
    return 0;
  });

  return (
    <div className={classes.root}>
      <div className={classes.labels}>
        <div></div>
        <div className={classes.holder}>
          <div className={classes.colorContainer}>
            <div
              className={classes.color}
              style={{ backgroundColor: '#5EE46B' }}
            />
            <div className={classes.colorLabel}>Verified</div>
          </div>
          <div className={classes.colorContainer}>
            <div
              className={classes.color}
              style={{ backgroundColor: '#775ee499' }}
            />
            <div className={classes.colorLabel}>Unverified</div>
          </div>
          <div className={classes.colorContainer}>
            <div
              className={classes.color}
              style={{ backgroundColor: '#E4765E' }}
            />
            <div className={classes.colorLabel}>Over 28 days</div>
          </div>
          <div className={classes.colorContainer}>
            <div
              className={classes.color}
              style={{ backgroundColor: '#D9D9D9' }}
            />
            <div className={classes.colorLabel}>Under 28 days</div>
          </div>
        </div>
      </div>
      <div className={classes.chart} ref={chartRef}>
        {allDates?.map(
          ({ history, dates, unknown, over28Days, unverified }) => (
            <div className={classes.rowContainer}>
              {dates.map(({ startDate, endDate }) => {
                const width = `${
                  moment.utc(endDate).diff(moment.utc(startDate), 'days') *
                  pixelsPerDay
                }`;

                const left = `${
                  today.diff(moment.utc(endDate), 'days') * pixelsPerDay
                }`;

                return (
                  <div
                    className={classes.row}
                    onClick={
                      onSectionClick
                        ? () => onSectionClick(history)
                        : undefined
                    }
                    onMouseOver={() => {
                      setHoverData({
                        placeName: getHistoryName(history),
                        lengthThere: `${moment
                          .utc(endDate)
                          .diff(
                            moment.utc(startDate),
                            'days'
                          )} days • ${moment
                          .utc(startDate)
                          .format('DD/MM/YYYY')} - ${moment
                          .utc(endDate)
                          .format('DD/MM/YYYY')}`
                      });
                    }}
                    onMouseOut={() => setHoverData(null)}
                    style={{
                      width: `${width}px`,
                      left: `${left}px`,
                      background:
                        (unknown && '#D9D9D9') ||
                        (over28Days && '#E4765E') ||
                        (unverified && '#775ee499') ||
                        '#5EE46B'
                    }}
                  />
                );
              })}
            </div>
          )
        )}
      </div>
      <div className={classes.hoverInfo}>
        <div className={classes.lengthThere}>
          {timelineStartDate.format('DD/MM/YYYY')}
        </div>
        <div className={classes.lengthThere}>
          {yearsAgo.format('DD/MM/YYYY')}
        </div>
      </div>

      <div className={classes.hoverInfo}>
        {hoverData && (
          <>
            <div className={classes.placeName}>{hoverData.placeName}</div>
            <div className={classes.lengthThere}>{hoverData.lengthThere}</div>
          </>
        )}
      </div>
    </div>
  );
};

export default createFragmentContainer(TimelineFrag, {
  history: graphql`
    fragment Timeline_history on BackgroundHistory @relay(plural: true) {
      uuid
      isArchived
      responseSendLogs {
        sentAt
        emailAddress
      }
      employer {
        uuid
        responseLink
        application {
          uuid
          name
          contactEmail
          jobTitle
          reasonForLeaving
          dates {
            startDate
            endDate
          }
          address {
            addressLine1
            addressLine2
            county
            postCode
            country
          }
          absent
        }
        response {
          uuid
          datesEmployed {
            startDate
            endDate
          }
          positionsHeld
          reasonForLeaving
          wouldEmploy
          wouldEmployReason
          absence
          datesAbsent {
            startDate
            endDate
          }
          otherInformation
          orgName
          orgContactName
          orgJobTitle
          orgTelephone
          orgAddress {
            addressLine1
            addressLine2
            county
            postCode
            country
          }
        }
      }
      education {
        uuid
        responseLink
        application {
          uuid
          name
          contactEmail
          dates {
            startDate
            endDate
          }
          examsTaken
          address {
            addressLine1
            addressLine2
            county
            postCode
            country
          }
        }
        response {
          uuid
          dates {
            startDate
            endDate
          }
          courseDetails
          examResults
          otherInformation
          orgName
          orgContactName
          orgJobTitle
          orgTelephone
          orgAddress {
            addressLine1
            addressLine2
            county
            postCode
            country
          }
        }
      }
      unemployed {
        uuid
        responseLink
        application {
          uuid
          dates {
            startDate
            endDate
          }
          reasonForUnemployment
          hadBenefits
        }
        response {
          uuid
          datesAccurate
          alternateDates {
            startDate
            endDate
          }
          lengthKnown
          relation
          inUK
          travelDetails
          employed
          employedDetails
          whereLived
          whatDoing
          financialSupport
          lacksIntegrity
          integrityDetail
          otherInformation
          occupation
          unavailableFrom
          firstName
          lastName
          daytimeTelephone
          homeTelephone
          address {
            addressLine1
            addressLine2
            county
            postCode
            country
          }
        }
      }
      selfEmployed {
        uuid
        responseLink
        application {
          uuid
          accountantName
          contactEmail
          dates {
            startDate
            endDate
          }
          address {
            addressLine1
            addressLine2
            county
            postCode
            country
          }
        }
        response {
          uuid
          dates {
            startDate
            endDate
          }
          selfEmploymentDetails
          businessName
          tradingName
          lacksIntegrity
          integrityDetail
          otherInformation
          orgName
          orgContactName
          orgJobTitle
          orgTelephone
          orgAddress {
            addressLine1
            addressLine2
            county
            postCode
            country
          }
        }
      }
    }
  `
});
